import {
  AfterViewChecked,
  Component,
  ElementRef,
  Host,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DateTime } from 'luxon';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { ReplaySubject, Subject } from 'rxjs';
import { Color } from '@app/shared/models/color.model';
import { ColumnHeaderDirective } from '@app/shared/components/organisms/table/directives/column-header.directive';
import { IconComponent, Size } from '@app/shared/components/atoms/icon/icon.component';
import { CommonModule } from '@angular/common';
import { CheckboxComponent } from '@app/shared/components/atoms/checkbox/checkbox.component';
import { BadgeNotificationComponent } from '@app/shared/components/atoms/badge-notification/badge-notification.component';

export interface FilterItem {
  name: string;
  checked: boolean;
}

@Component({
  selector: 'app-column-header',
  templateUrl: './column-header.component.html',
  styleUrls: ['./column-header.component.scss'],
  imports: [CommonModule, IconComponent, CheckboxComponent, BadgeNotificationComponent],
  standalone: true,
})
export class ColumnHeaderComponent implements OnInit, AfterViewChecked, OnDestroy {
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() name?: string;

  @Input() sort?: 'asc' | 'desc';

  /*
    This field must be populated when a type is present
    It must contain the path to the field to sort or filter
   */
  @Input() field? = 'value';

  @Input() type?: 'filter' | 'sort';

  @Input() filterTranslate?: (value: string) => string;

  @HostBinding('style.float') float?: 'right' | 'left';

  @ViewChild('connectedEl') protected originEl!: ElementRef;

  menuVisible = false;

  icon?: string;

  lastModifiedDate = 0;

  @Input() filterItems?: FilterItem[] = [];

  private hover: boolean = false;

  private $source = new ReplaySubject(1);

  constructor(
    @Host() directive: ColumnHeaderDirective,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Host() private parent: BaseTableComponent<any>,
  ) {
    this.float = directive.textAlign === 'end' ? 'right' : 'left';
  }

  ngOnInit(): void {
    this.updateIcon();

    if (this.sort) {
      this.toSort();
    }
  }

  updateCount() {
    const { parent, name } = this;
    const { headerFilters } = parent;
    let { source } = parent;

    headerFilters
      .filter(({ type }) => type === 'filter')
      .filter((filter) => filter.name !== name)
      .forEach(({ field, values }) => {
        source = source.filter((item) => {
          if (!values || !values.length) return true;

          const value = this.searchFieldValue(item, field!);
          return values?.includes(value);
        });
      });
  }

  updateIcon() {
    const { type, sort, activeFilters } = this;

    const { length: containsFilter } = activeFilters;
    switch (type) {
      case 'filter':
        this.icon = `ph-funnel ${containsFilter > 0 && 'active'}`;
        break;
      case 'sort':
        if (!sort) this.icon = 'ph-arrows-down-up';
        else if (sort === 'asc') this.icon = 'ph-arrow-up active';
        else this.icon = 'ph-arrow-down active';
        break;
      default:
        break;
    }
  }

  toSort() {
    this.updateIcon();
    this.lastModifiedDate = DateTime.now().toMillis();
  }

  switchSort() {
    const { sort } = this;

    this.sort = sort === 'asc' ? 'desc' : 'asc';

    this.toSort();

    return this.sort;
  }

  extractOptions() {
    const { name, sort, field, type, lastModifiedDate, activeFilters: values } = this;

    return {
      name,
      sort,
      field,
      type,
      lastModifiedDate,
      values,
    };
  }

  removeSort() {
    this.sort = undefined;
    this.updateIcon();

    return this.sort;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private searchFieldValue(obj: any, path: string) {
    let current = obj;
    path.split('.').forEach((p) => {
      current = current[p];
    });
    return current;
  }

  toggleMenu(): void {
    this.menuVisible = !this.menuVisible;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.originEl.nativeElement.contains(event.target)) {
      this.menuVisible = false;
    }
  }

  onFilterItemClick({ name, checked }: FilterItem) {
    this.filterItems!.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      if (item.name === name) item.checked = !checked;
    });
    this.updateIcon();
    this.lastModifiedDate = DateTime.now().toMillis();
  }

  onKeyDownFilterItem(item: FilterItem) {
    this.onFilterItemClick(item);
  }

  private get activeFilters(): string[] {
    const { filterItems } = this;

    if (!filterItems) {
      return [];
    }

    return filterItems.filter(({ checked }) => checked).map(({ name }) => name);
  }

  protected get activeFiltersNumber(): number {
    return this.activeFilters.length;
  }

  get showIcon() {
    const { icon, type, filterItems, parent } = this;

    if (parent.isLoading || !parent.source.length) return false;
    if (type === 'filter' && filterItems!.length === 0) return false;

    return icon !== undefined;
  }

  get isActive() {
    const { sort, activeFilters } = this;
    if (sort && ['asc', 'desc'].includes(sort)) return true;
    return activeFilters.length > 0;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.hover = true;
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hover = false;
  }

  get iconColor() {
    const { hover } = this;
    const { primary400, primary500, grey500 } = Color;

    if (hover) return this.isActive ? primary400 : primary500;

    return this.isActive ? primary400 : grey500;
  }

  ngAfterViewChecked(): void {
    const { parent, type, $source } = this;
    const { source } = parent;

    if (type !== 'filter') return;

    $source.next(source);
  }

  ngOnDestroy(): void {
    this.$source.complete();
    this.unsubscribe$.next(true);
  }

  protected readonly Color = Color;

  protected readonly Size = Size;

  protected readonly Number = Number;
}
